import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";

// Authentication layout components
import { useAuth0 } from "@auth0/auth0-react";
import SoftBox from "components/SoftBox";
import brand from "assets/images/sign-in-logo.png";
import { generateOtp, verifyOtp } from "api/Auth0Api";

function TwoStepAuthentication() {
  const navigate = useNavigate();
  const { user } = useAuth0();
  const [otp, setOtp] = useState("");
  const [onError, setOnError] = useState(false);
  const [resendOtp, setResendOtp] = useState(false);
  const [otpExpired, setOtpExpired] = useState(false);
  const [timer, setTimer] = useState(60);
  const [newOtpSent, setNewOtpSent] = useState(false);

  const handleOtpVerification = async (e) => {
    e.preventDefault();

    otpExpired && setOtpExpired(false)

    try {
      const response = await verifyOtp(user?.cxfUserId, user?.email, user?.stripeId, otp);
      if (response.isSuccessful) {
        localStorage.setItem('isOtpVerified', 'true');
        navigate("/home");
        setOnError(false);
      } else if (response.resultObject === null) {
        setOnError(true);
      } else {
        setOtpExpired(true);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleOTPChange = (e) => {
    onError && setOnError(false);
    newOtpSent && setNewOtpSent(false)

    const value = e.target.value;
    if (/^\d*$/.test(value) && value.length <= 6) {
      setOtp(value);
    }
  };

  const handleResend = async () => {
    onError && setOnError(false)
    otpExpired && setOtpExpired(false)

    try {
      const response = await generateOtp(user?.cxfUserId, user?.email, user?.stripeId);
      if (response.isSuccessful) {
        setResendOtp(true);
        setOtp("");
        setTimer(60);
        setNewOtpSent(true);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    let countdown;
    if (resendOtp) {
      countdown = setInterval(() => {
        setTimer((prevTimer) => (prevTimer > 0 ? prevTimer - 1 : 0));
      }, 1000);
    }

    if (timer === 0) {
      setResendOtp(false);
      clearInterval(countdown);
    }

    return () => clearInterval(countdown);
  }, [resendOtp, timer]);

  useEffect(() => {
    const handleGenerateOtp = async (auth0_id, email, tenant_id) => {
      try {
        await generateOtp(auth0_id, email, tenant_id);
      } catch (error) {
        console.log(error);
      }
    };

    handleGenerateOtp(user?.cxfUserId, user?.email, user?.stripeId);
  }, []);

  useEffect(() => {
    const checkVerificationStatus = async () => {
      try {
        const isOtpVerified = localStorage.getItem('isOtpVerified');
        if (isOtpVerified) {
          navigate('/home');
        }
      } catch (error) {
        console.error('Error checking verification status:', error);
      }
    };

    checkVerificationStatus();
  }, []);

  return (
    <form
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      }}
      onSubmit={(e) => handleOtpVerification(e)}
    >
        <SoftBox
          component="img"
          src={brand}
          alt={"CX Fabric"}
          width="200px"
          sx={{
            maxHeight: "60px",
            objectFit: "cover",
            marginTop: "40px"
          }}
        />
        <SoftTypography
          variant="h3"
          sx={{
            fontWeight: "normal",
            color: "#1e212a",
            marginTop: "50px",
            fontSize: "24px"
          }}
        >
          Verify Your Identity
        </SoftTypography>
        <SoftTypography
          sx={{
            fontSize: "14px",
            color: "#8d8d8d",
            textAlign: "center",
            marginTop: "10px"
          }}
        >
          Check your email which contains OTP login code.<br />
          Code will expire in 5 minutes.
        </SoftTypography>

        <div style={{marginTop: '20px', paddingBottom: '30px', width: '80%'}} className={"otp-input"}>
          {newOtpSent && <SoftTypography
            sx={{
              fontSize: "12px",
              color: "#00cd00",
              textAlign: "center",
              marginBottom: "5px"
            }}
          >
            A new OTP code has been generated
          </SoftTypography>
          }

          <SoftInput
            size="medium"
            placeholder="Enter your one-time code"
            value={otp}
            onChange={handleOTPChange}
            required
            inputProps={{
              style: { 
              textAlign: 'center',
              },
            }}
          />
          {onError &&
          <p style={{fontSize: "13px", marginTop: "5px", color: "#ff667c"}}>
            The one-time code you have entered does not match
          </p>
          }
          {otpExpired &&
          <p style={{fontSize: "12px", marginTop: "5px", color: "#ff667c"}}>
            The one-time code has expired. Please request a new one
          </p>
          }
        </div>

        <SoftButton
          variant="gradient"
          size="medium"
          color="info"
          data-testid="verify-otp"
          type="submit"
          sx={{
            width: "80%",
            borderRadius: "3px",
            textTransform: "capitalize",
            fontSize: "16px",
            fontWeight: "normal",
            height: "52px"
          }}
        >
            Continue
        </SoftButton>

        <SoftTypography
          sx={{
            fontSize: "14px",
            color: "#753bef",
            textAlign: "center",
            fontWeight: "bold",
            marginTop: "20px",
            cursor: !resendOtp ? "pointer" : "default",
            textDecoration: "underline"
          }}
          onClick={!resendOtp ? handleResend : null}
        >
          <a>{resendOtp ? `Resend Code in ${timer}s` : "Resend Code"}</a>
        </SoftTypography>
    </form>
  );
}

export default TwoStepAuthentication;
